import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { useUpdateIntegrationSource } from '../hooks'
import type { IntegrationSource } from '../types'
import { SourceConfigFields } from './SourceConfigFields'
import { readSourceConfigDraft, buildSourceConfig, type SourceConfigDraft } from './sourceConfigDraft'

type Props = {
  source: IntegrationSource
  onClose: () => void
}

/**
 * Edycja źródła: aktywność + pełny config (auth, encja docelowa, intake contract, CORS).
 * Te same pola co przy tworzeniu (SourceConfigFields). Wartości wczytane z source.config.
 * Uwaga: backend merguje config - opróżnienie pola NIE usuwa klucza (poza patterns).
 */
export function EditSourceDialog({ source, onClose }: Props) {
  const update = useUpdateIntegrationSource()
  const [isActive, setIsActive] = useState(source.isActive)
  const [draft, setDraft] = useState<SourceConfigDraft>(
    readSourceConfigDraft(source.config ?? {}, source.allowedOriginPatterns),
  )
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    const { config, patterns, error: cfgError } = buildSourceConfig(draft)
    if (cfgError) {
      setError(cfgError)
      return
    }
    update.mutate(
      { code: source.code, body: { isActive, allowedOriginPatterns: patterns, config } },
      { onSuccess: onClose, onError: e => setError(formatError(e)) },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded bg-white p-5 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-semibold">{source.name}</Dialog.Title>
              <Dialog.Description className="text-xs text-slate-500 font-mono mt-0.5">
                {source.code} · {source.authType}
              </Dialog.Description>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Zamknij">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                disabled={update.isPending}
                className="size-4"
              />
              <span className="text-sm">Aktywne (POST events działa)</span>
            </label>

            <SourceConfigFields value={draft} onChange={setDraft} disabled={update.isPending} />

            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={update.isPending}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={update.isPending}
              className="rounded bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {update.isPending ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response: { data?: { title?: string; detail?: string } } }).response
    return r?.data?.detail ?? r?.data?.title ?? 'Błąd zapisu'
  }
  return 'Nieznany błąd'
}
